import { motion, useScroll, useTransform } from "framer-motion";
import { useRef, type ReactNode } from "react";

interface ImageSectionProps {
  backgroundImage?: string;
  children: ReactNode;
  align?: "left" | "center";
  overlay?: boolean;
  className?: string;
  id?: string;
  /** Use full viewport height (hero) vs min-height */
  fullHeight?: boolean;
}

const ImageSection = ({
  backgroundImage,
  children,
  align = "left",
  overlay = true,
  className = "",
  id,
  fullHeight = false,
}: ImageSectionProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "center center"],
  });

  const opacity = useTransform(scrollYProgress, [0, 0.6], [0, 1]);
  const y = useTransform(scrollYProgress, [0, 0.6], [50, 0]);

  return (
    <section
      id={id}
      ref={ref}
      className={`relative ${fullHeight ? "h-[100svh]" : "min-h-[90svh]"} flex items-center overflow-hidden ${className}`}
      style={
        backgroundImage
          ? {
              backgroundImage: `url("${backgroundImage}")`,
              backgroundSize: "cover",
              backgroundPosition: "center right",
              backgroundRepeat: "no-repeat",
            }
          : undefined
      }
    >
      {/* Placeholder bg when no image */}
      {!backgroundImage && <div className="absolute inset-0 z-0 bg-muted" />}

      {/* Soft readability gradient — never darkens the right side */}
      {overlay && (
        <div
          className="absolute inset-0 z-[1] pointer-events-none"
          style={{
            background:
              "linear-gradient(to right, rgba(15,23,42,0.45) 0%, rgba(15,23,42,0.25) 30%, rgba(15,23,42,0.12) 55%, rgba(15,23,42,0) 75%)",
          }}
        />
      )}

      <motion.div
        style={fullHeight ? undefined : { opacity, y }}
        className={`relative z-10 w-full ${
          align === "left"
            ? "max-w-[600px] pl-8 md:pl-16 lg:pl-24 text-left"
            : "max-w-[520px] mx-auto text-center px-6"
        }`}
      >
        {children}
      </motion.div>
    </section>
  );
};

export default ImageSection;
